import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, TableBody, TableHead, FormControlLabel, Checkbox } from '@mui/material';
import { StyledTable, StyledTableRow, StyledHeaderCell, StyledTableBodyCell } from 'components/common/Styles';
import { Button } from '@totalsoft/rocket-ui';
import { useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { ADMIN_ITEMS_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const ImportValuesModal = ({ onCloseModal, onLoadPropertyDetails, propertyDetails, isPropertyDetailSelected, onImportValues }) => {
  const { t } = useTranslation();
  const [newSelections, setNewSelections] = useState([]);
  const [selectAllChecked, setSelectAllChecked] = useState(propertyDetails.every(detail => isPropertyDetailSelected(detail.propertyValue)));
  const canWrite = usePermission(ADMIN_ITEMS_WRITE);

  const allValuesSelected = useCallback(() => {
    return propertyDetails.every(detail => isPropertyDetailSelected(detail.propertyValue));
  }, [isPropertyDetailSelected, propertyDetails]);

  const handleSelectionChanged = useCallback(
    (propertyValue, checked) => {
      checked === false
        ? setNewSelections(prev => {
            var selections = prev.filter(x => x !== propertyValue);
            setSelectAllChecked(selections?.length === propertyDetails?.length);
            return selections;
          })
        : setNewSelections(prev => {
            var selections = [...prev, propertyValue];
            setSelectAllChecked(selections?.length === propertyDetails?.length);
            return selections;
          });
    },
    [propertyDetails?.length]
  );

  const handleAllSelection = useCallback(() => {
    if (!selectAllChecked) {
      const newSelections = propertyDetails
        .filter(detail => !isPropertyDetailSelected(detail.propertyValue))
        .map(detail => detail.propertyValue);

      setNewSelections(prevSelections => [...prevSelections, ...newSelections]);
    } else {
      const valuesToRemove = propertyDetails
        .filter(detail => newSelections.includes(detail.propertyValue))
        .map(detail => detail.propertyValue);

      setNewSelections(prevSelections => prevSelections.filter(value => !valuesToRemove.includes(value)));
    }

    setSelectAllChecked(prev => !prev);
  }, [propertyDetails, selectAllChecked, isPropertyDetailSelected, newSelections]);

  useEffect(() => {
    const load = async () => {
      await onLoadPropertyDetails();
    };
    load();
    // eslint-disable-next-line
  }, []);

  return (
    <Dialog open fullWidth maxWidth={'sm'} onClose={onCloseModal}>
      <DialogTitle>{t('Items.Values.Import.ImportValues')}</DialogTitle>
      <DialogContent>
        {propertyDetails && (
          <>
            {propertyDetails.length > 0 && (
              <Grid item container xs={12}>
                <FormControlLabel
                  disabled={allValuesSelected() || !canWrite}
                  control={<Checkbox id={'Values_All_Selected'} checked={selectAllChecked} onChange={handleAllSelection} />}
                  label={t('Items.Values.Import.SelectAllValues')}
                />
              </Grid>
            )}
            <Grid container justifyContent={'center'}>
              {propertyDetails && propertyDetails.length > 0 ? (
                <StyledTable>
                  <TableHead>
                    <StyledTableRow>
                      <StyledHeaderCell style={{ textAlign: 'start', width: '30%' }}>{t('Select')}</StyledHeaderCell>
                      <StyledHeaderCell style={{ textAlign: 'start', width: '70%' }}>{t('Items.Values.Import.Value')}</StyledHeaderCell>
                    </StyledTableRow>
                  </TableHead>
                  <TableBody>
                    {propertyDetails.map((detail, idx) => {
                      return (
                        <StyledTableRow key={idx}>
                          <StyledTableBodyCell style={{ textAlign: 'start', width: '30%' }} size={'small'}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={
                                    isPropertyDetailSelected(detail.propertyValue) || newSelections?.some(x => x === detail.propertyValue)
                                  }
                                  onChange={e => handleSelectionChanged(detail.propertyValue, e.target.checked)}
                                  color='primary'
                                  disabled={isPropertyDetailSelected(detail.propertyValue) || !canWrite}
                                />
                              }
                            />
                          </StyledTableBodyCell>
                          <StyledTableBodyCell style={{ textAlign: 'start', width: '70%' }}>{detail.propertyValue}</StyledTableBodyCell>
                        </StyledTableRow>
                      );
                    })}
                  </TableBody>
                </StyledTable>
              ) : (
                <>{t('Items.Values.Import.NoDetailsConfigured')}</>
              )}
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ display: 'flex', gap: 1 }}>
        <Button
          color={'primary'}
          onClick={() => {
            onImportValues(newSelections);
            onCloseModal();
          }}
          disabled={newSelections.length === 0}
        >
          {t('Items.Values.Import.ImportButton')}
        </Button>
        <Button color={'primary'} onClick={onCloseModal}>
          {t('Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImportValuesModal.propTypes = {
  onCloseModal: PropTypes.func,
  onLoadPropertyDetails: PropTypes.func,
  propertyDetails: PropTypes.array,
  isPropertyDetailSelected: PropTypes.func,
  onImportValues: PropTypes.func
};

export default ImportValuesModal;
